import SvgColor from '@/components/SvgColor';
import { paths } from '@/constants';
import { useUiFeatureFlags } from '@/context/FeatureFlagsContext';
import { CustomerPermissionsRole, CustomerType } from '@/enums';
import { useCanCreateOrder } from '@/hooks/usePermissions';
import { useUserProfile } from '@/hooks/useUsers';
import { useMemo } from 'react';

const icon = (name: string) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
  // OR
  // <Iconify icon="fluent:mail-24-filled" />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

export const ICONS = {
  blog: icon('ic-blog'),
  user: icon('ic-user'),
  file: icon('ic-file'),
  banking: icon('ic-banking'),
  analytics: icon('ic-analytics'),
  dashboard: icon('ic-dashboard'),
};

function getOrderChildren(roles: string[], canCreateOrder: boolean) {
  const children: Array<{ title: string; path: string }> = [];

  if (canCreateOrder) {
    children.push({ title: 'Create New Order', path: `${paths.orders}/create` });
  }

  if (roles.includes(CustomerPermissionsRole.BulkOrderUploader)) {
    children.push({ title: 'Create Bulk Order', path: `${paths.orders}/upload` });
  }

  return children;
}

export function useNavData() {
  const { organizationCustomerType, organizationRoles } = useUserProfile();
  const { flags, isLoading } = useUiFeatureFlags();
  const canCreateOrder = useCanCreateOrder();

  return useMemo(
    () => [
      {
        items: [
          {
            title: 'Home',
            path: paths.home,
            icon: ICONS.dashboard,
          },
          {
            title: 'Orders',
            path: paths.orders,
            icon: ICONS.file,
            children:
              organizationCustomerType === CustomerType.lender
                ? getOrderChildren(organizationRoles, canCreateOrder)
                : [],
          },
          ...(organizationCustomerType === CustomerType.admin
            ? [
                {
                  title: 'Reports',
                  path: paths.reports,
                  icon: ICONS.analytics,
                },
              ]
            : []),
          ...(organizationCustomerType === CustomerType.admin
            ? [
                {
                  title: 'Lender Management',
                  path: paths.lenders,
                  icon: ICONS.banking,
                },
              ]
            : []),

          ...(organizationCustomerType === CustomerType.admin && flags.viewVendors && !isLoading
            ? [{ title: 'Vendor Management', path: paths.vendors, icon: ICONS.blog }]
            : []),

          ...(organizationRoles.includes(CustomerPermissionsRole.AllocationAdmin) && flags.viewAllocation && !isLoading
            ? [{ title: 'Allocation Management', path: paths.allocations, icon: ICONS.blog }]
            : []),
        ],
      },
    ],
    [organizationCustomerType, organizationRoles, flags.viewVendors, flags.viewAllocation, isLoading, canCreateOrder]
  );
}
